import { formatShortDateWithWeekday, formatTime } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { HourlyPayLabel } from "@src/appV2/redesign/Pay/HourlyPayLabel";
import { TotalPayLabel } from "@src/appV2/redesign/Pay/TotalPayLabel";
import { InstantPayPill } from "@src/appV2/redesign/Shift/InstantPayPill";
import { ShiftCardPillsWrapper } from "@src/appV2/redesign/Shift/PillsWrapper";
import { ShiftQualificationLabel } from "@src/appV2/redesign/Shift/QualificationLabel";
import { BookingCardSummaryWrapper } from "@src/appV2/redesign/Shift/SummaryWrapper";
import { Text } from "@src/appV2/redesign/system/ui-components";
import pluralize from "pluralize";

import { DurationWithBreakDisplay } from "../../components/DurationWithBreakDisplay";
import { type ShiftBlockWithMappedDetails } from "./api/useFetchPaginatedShiftBlocks";
import { getFinalPayForShiftBlock, getHourlyPayForShiftBlock } from "./utils";

interface ShiftBlockHeaderProps {
  shiftBlockWithDetails: ShiftBlockWithMappedDetails;
  workerTimezone: string;
  showStartEndTime?: boolean;
}

export function ShiftBlockHeader(props: ShiftBlockHeaderProps) {
  const { shiftBlockWithDetails, workerTimezone, showStartEndTime = true } = props;
  const { blockStartTime, blockEndTime, shifts, agentReq, orientationBlockId } =
    shiftBlockWithDetails;
  const finalPay = getFinalPayForShiftBlock(shiftBlockWithDetails);
  const [firstShiftDetails] = shifts;
  const shiftsLength = shifts.length;
  const lastShiftDetails = shifts[shiftsLength - 1];
  const hourlyPay = getHourlyPayForShiftBlock(shiftBlockWithDetails);
  const showApproximatePaySymbol = shifts.some((shift) => {
    const shiftPay = shift.offer?.pay?.value ?? shift.pay;
    return shiftPay !== hourlyPay;
  });

  const isOrientationBlock = isDefined(orientationBlockId);

  const formattedHourlyPay = `${showApproximatePaySymbol ? "~" : ""}${formatDollarsAsUsd(hourlyPay, { minimumFractionDigits: 2 })}`;
  const formattedTotalPay = formatDollarsAsUsd(finalPay, { minimumFractionDigits: 2 });

  return (
    <Stack gap={1}>
      <BookingCardSummaryWrapper sx={{ justifyContent: "space-between" }}>
        <ShiftCardPillsWrapper>
          {firstShiftDetails.isInstantPay && <InstantPayPill />}
          {isOrientationBlock && (
            <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
              Orientation Block
            </Text>
          )}
        </ShiftCardPillsWrapper>

        <Stack spacing={1} direction="column" alignItems="flex-end">
          <HourlyPayLabel hourlyPay={formattedHourlyPay} size="small" />
          <TotalPayLabel
            totalPay={formattedTotalPay}
            sx={(theme) => ({ color: theme.palette.text.tertiary })}
          />
        </Stack>
      </BookingCardSummaryWrapper>

      <Stack gap={1}>
        <Text variant="body2">
          {formatShortDateWithWeekday(firstShiftDetails.start, { timeZone: workerTimezone })} -{" "}
          {formatShortDateWithWeekday(lastShiftDetails.start, { timeZone: workerTimezone })}
        </Text>

        {showStartEndTime && (
          <Stack gap={0.5}>
            <Text semibold variant="body1">
              {formatTime(blockStartTime, { timeZone: workerTimezone })} -{" "}
              {formatTime(blockEndTime, { timeZone: workerTimezone })}
            </Text>
            <DurationWithBreakDisplay
              totalDurationInHours={firstShiftDetails.time}
              breakDurationInMinutes={firstShiftDetails.breakPolicy?.breakDurationInMinutes ?? 0}
              requiresBreak={isDefined(firstShiftDetails.breakPolicy)}
            />
          </Stack>
        )}
      </Stack>

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <ShiftQualificationLabel qualificationText={agentReq} />
        <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
          {shifts.length} {pluralize("shift", shifts.length)}
        </Text>
      </Stack>
    </Stack>
  );
}
